import { useEffect, useState } from "react";
import Skeleton from "react-loading-skeleton";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import { BackButton } from "../../../../components/buttons/backButton";
import { Button } from "../../../../components/buttons/button";
import { ProfileIcon } from "../../../../components/cards/profilePic";
import { Icon } from "../../../../components/icon/icon";
import { ConfirmationModal } from "../../../../components/modal/app_modals/confirmationModal";
import { ManageSalesAgentModal } from "../../../../components/modal/app_modals/manangeSalesAgentModal";
import { TabNav } from "../../../../components/tabNav";
import { BgWrapper } from "../../../../components/wrapper/bgWrapper";
import { useModalControl } from "../../../../customeHooks/useModalControl";
import {
  useGetSingleSalesAgentQuery,
  useRemoveSalesAgentMutation,
} from "../../../../redux/features/admin/sales/sales.api";
import {
  SKELETON_BASE_COLOR,
  SKELETON_HIGHLIGHT_COLOR,
} from "../../../../utils/constants";
import { formatDate, formatToCurrency } from "../../../../utils/helpers";
import { CommissionsHistory } from "./commisionHistory";
import { CommissionsBreakdown } from "./commissionsBreakdown";
import { LinkedOrganizers } from "./linkedOrganizers";

type Tab = "commissionsBreakdown" | "linkedOrganizers" | "commissionHistory";

const tabs: {
  title: string;
  to: Tab;
}[] = [
  {
    title: "Commission Breakdown",
    to: "commissionsBreakdown",
  },
  {
    title: "Linked Organizers",
    to: "linkedOrganizers",
  },
  {
    title: "Commission History",
    to: "commissionHistory",
  },
];

export const SingleSalesAgentPage = () => {
  const [activeTab, setActiveTab] = useState<Tab>("commissionsBreakdown");

  const navigate = useNavigate();

  const { id } = useParams<{ id: string }>();

  const [searchParams, setSearchParams] = useSearchParams();

  const { modal, modalOpen, handleModalClose, handleModalOpen } =
    useModalControl();

  const {
    data: salesAgentData,
    isFetching: fetchingSalesAgentData,
    refetch: refetchSalesAgentData,
  } = useGetSingleSalesAgentQuery(
    {
      id: id!,
    },
    {
      refetchOnMountOrArgChange: true,
      skip: !id,
    },
  );

  const [removeSalesAgent, { isLoading: removingSalesAgent }] =
    useRemoveSalesAgentMutation();

  useEffect(() => {
    setActiveTab((searchParams.get("tab") ?? "commissionsBreakdown") as Tab);
  }, [searchParams]);

  return fetchingSalesAgentData ? (
    <div className="flex flex-col gap-8">
      <BackButton />

      <BgWrapper>
        <BgWrapper className="flex flex-wrap items-start gap-5">
          <div className="w-full lg:w-fit">
            <Skeleton
              width={64}
              height={64}
              circle
              baseColor={SKELETON_BASE_COLOR}
              highlightColor={SKELETON_HIGHLIGHT_COLOR}
            />
          </div>

          <div className="flex flex-1 flex-col gap-1">
            <Skeleton
              width={"100%"}
              height={32}
              baseColor={SKELETON_BASE_COLOR}
              highlightColor={SKELETON_HIGHLIGHT_COLOR}
              containerClassName="w-full"
            />

            <Skeleton
              width={"50%"}
              height={24}
              baseColor={SKELETON_BASE_COLOR}
              highlightColor={SKELETON_HIGHLIGHT_COLOR}
              containerClassName="w-full"
            />

            <Skeleton
              width={"50%"}
              height={24}
              baseColor={SKELETON_BASE_COLOR}
              highlightColor={SKELETON_HIGHLIGHT_COLOR}
              containerClassName="w-full"
            />

            <Skeleton
              width={"50%"}
              height={24}
              baseColor={SKELETON_BASE_COLOR}
              highlightColor={SKELETON_HIGHLIGHT_COLOR}
              containerClassName="w-full"
            />
          </div>
        </BgWrapper>
      </BgWrapper>
    </div>
  ) : (
    <>
      <div className="flex flex-col gap-8">
        <BackButton />

        <div className="flex flex-col gap-0.5">
          <BgWrapper className="flex flex-wrap items-start gap-5 !px-4 !py-5">
            <div className="w-full lg:w-fit">
              <ProfileIcon dimension={64} className="!p-3" />
            </div>

            <div className="flex flex-1 flex-col gap-1 text-base font-medium text-grey-600">
              <p className="text-2xl font-bold text-grey-900">
                {salesAgentData?.data?.firstName}{" "}
                {salesAgentData?.data?.lastName}
              </p>
              <p className="flex items-center gap-1.5">
                <Icon svg="mail" width={16} height={16} />
                {salesAgentData?.data?.email}
              </p>
              <p className="flex items-center gap-1.5">
                <Icon svg="percent" width={16} height={16} />
                {formatToCurrency(salesAgentData?.data?.commission?.NGN ?? 0, {
                  currency: "NGN",
                })}{" "}
                ∙{" "}
                {formatToCurrency(salesAgentData?.data?.commission?.CAD ?? 0, {
                  currency: "CAD",
                })}{" "}
                per paid ticket
              </p>
              <p className="flex items-center gap-1.5">
                <Icon svg="calendar-2" width={16} height={16} />
                Joined on {formatDate(salesAgentData?.data?.createdAt)}
              </p>
            </div>

            <div className="flex items-center gap-2.5">
              <Button
                type="button"
                text="Edit"
                icon="pencil"
                variation="plain-grey"
                onClick={() => {
                  handleModalOpen("editAgentModal");
                }}
              />

              <Button
                type="button"
                text="Remove"
                icon="bin"
                variation="plain-danger"
                onClick={() => {
                  handleModalOpen("removeAgentModal");
                }}
              />
            </div>
          </BgWrapper>

          <BgWrapper className="!rounded-b-none">
            <TabNav
              navs={tabs.map((t) => {
                return {
                  ...t,
                  onClick: () => {
                    setSearchParams({
                      tab: t.to,
                    });
                  },
                };
              })}
              activeTab={activeTab}
              variant="switcher"
              className=""
              tabClassName=""
            />
          </BgWrapper>

          {activeTab === "commissionsBreakdown" && !!id ? (
            <CommissionsBreakdown agentId={id} />
          ) : undefined}

          {activeTab === "linkedOrganizers" && !!id ? (
            <LinkedOrganizers agentId={id} />
          ) : undefined}

          {activeTab === "commissionHistory" && !!id ? (
            <CommissionsHistory agentId={id} />
          ) : undefined}
        </div>
      </div>

      <ManageSalesAgentModal
        open={modalOpen && modal === "editAgentModal"}
        handleClose={() => {
          handleModalClose();
        }}
        onSuccess={async () => {
          await refetchSalesAgentData();
          handleModalClose();
        }}
        agent={salesAgentData?.data}
      />

      {salesAgentData?.data?._id ? (
        <ConfirmationModal
          open={modalOpen && modal === "removeAgentModal"}
          handleClose={() => {
            handleModalClose();
          }}
          loading={removingSalesAgent}
          onProceed={async () => {
            const resp = await removeSalesAgent({
              id: salesAgentData?.data?._id,
            });

            if (resp.data?.status === "success") {
              navigate("/sales");
            }
          }}
          title={`Remove Sales Agent`}
          subtitle={`You're about to remove "${salesAgentData?.data?.firstName} ${salesAgentData?.data?.lastName}" from their role as sales agent`}
          proceedText="Remove"
          proceedVariation="danger"
        />
      ) : undefined}
    </>
  );
};
